import type { ReactNode } from "react";
import { PhoneNav } from "../../shared/phone/PhoneNav";
import { Segmented } from "../../shared/phone/Row";
import type { PartnerProgramMembership, PartnerSignatureInput } from "../../shared/types";
import { agreementClauses } from "../lib/agreement";
import { Icon } from "../components/phone/Icon";
import { ProgramTile } from "../components/phone/parts";
import { SignSheet } from "../components/phone/SignSheet";
import { toast } from "../components/phone/Toast";

export type StepKey = "about" | "agreement" | "submitted" | "tax" | "payout" | "done";
export type PayKind = "paypal" | "bank" | "wise";
type SignTarget = "agreement" | "tax";

const TAX_CLASSES = [
  "Individual / sole proprietor",
  "Single-member LLC",
  "C corporation",
  "S corporation",
  "Partnership",
  "Trust or estate",
  "LLC",
];

/** Everything the setup steps hold and do; the wizard owns it, this only draws it. */
interface Onboarding {
  membership: PartnerProgramMembership;
  product: string;
  partnerName: string;
  today: string;
  flow: StepKey[];
  idx: number;
  busy: boolean;
  error: string | null;
  canContinue: boolean;
  advance: () => void;
  stepBack: () => void;
  /** Where back goes from the first step, or null when there is nowhere to go. */
  leave: (() => void) | null;
  audienceSize: string;
  setAudienceSize: (v: string) => void;
  mainChannel: string;
  setMainChannel: (v: string) => void;
  promoPlan: string;
  setPromoPlan: (v: string) => void;
  agreementSig: PartnerSignatureInput | null;
  taxForm: "W-9" | "W-8BEN";
  setTaxForm: (v: "W-9" | "W-8BEN") => void;
  legalName: string;
  setLegalName: (v: string) => void;
  country: string;
  setCountry: (v: string) => void;
  taxClass: string;
  setTaxClass: (v: string) => void;
  taxAddress: string;
  setTaxAddress: (v: string) => void;
  taxpayerId: string;
  setTaxpayerId: (v: string) => void;
  certified: boolean;
  setCertified: (v: boolean) => void;
  taxSig: PartnerSignatureInput | null;
  payKind: PayKind;
  setPayKind: (v: PayKind) => void;
  payValue: string;
  setPayValue: (v: string) => void;
  signOpen: boolean;
  signTarget: SignTarget;
  openSign: (t: SignTarget) => void;
  closeSign: () => void;
  onAdopt: (sig: PartnerSignatureInput) => void;
}

const PAY: Record<PayKind, { seg: string; label: string; missing: string }> = {
  paypal: { seg: "PayPal", label: "PayPal email", missing: "Add your PayPal email to continue" },
  bank: { seg: "Bank", label: "Account number", missing: "Add your account number to continue" },
  wise: { seg: "Wise", label: "Wise email or account", missing: "Add your Wise email or account to continue" },
};

function Field({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="pn-field">
      <label htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

/** A signature on the page: drawn ink, or a typed name in a hand. */
function Ink({ sig }: { sig: PartnerSignatureInput }) {
  if (sig.kind === "drawn" && sig.dataUrl) {
    return (
      <span className="po-ink-w">
        <img className="po-ink" src={sig.dataUrl} alt="Your signature" />
      </span>
    );
  }
  return <span className="po-typed">{sig.name}</span>;
}

function SignField({ sig, who, today, onSign }: { sig: PartnerSignatureInput | null; who: string; today: string; onSign: () => void }) {
  if (!sig) {
    return (
      <button type="button" className="po-sigf" onClick={onSign}>
        <span className="po-sig-tag">Sign</span>
        <span>Sign here</span>
      </button>
    );
  }
  return (
    <div className="po-signed">
      <Ink sig={sig} />
      <span className="po-sig-cap">
        {sig.name || who}, {today}
      </span>
    </div>
  );
}

function Head({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="po-obh">
      <h1>{title}</h1>
      {sub ? <p>{sub}</p> : null}
    </div>
  );
}

/** What is still missing on this step, said in one short line above Continue. */
function missing(o: Onboarding, key: StepKey): ReactNode {
  const open = (t: SignTarget, words: string) => (
    <>
      <button type="button" className="po-hl" onClick={() => o.openSign(t)}>
        {words}
      </button>{" "}
      to continue
    </>
  );
  if (key === "agreement" && !o.agreementSig) return open("agreement", "Sign the agreement");
  if (key === "tax") {
    if (!o.legalName.trim()) return "Add your legal name to continue";
    if (!o.certified && !o.taxSig) return "Certify and sign to continue";
    if (!o.certified) return "Tick the certify box to continue";
    if (!o.taxSig) return open("tax", "Sign the form");
  }
  if (key === "payout" && !o.payValue.trim()) return PAY[o.payKind].missing;
  return null;
}

async function copyLink(link: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(link);
    toast("Link copied", "copy");
  } catch {
    toast("Copying is blocked here. Press and hold to copy.", "info");
  }
}

/**
 * Setting up on a phone and an iPad: "Step 2 of 5" with a thin progress bar, back as the chevron, one
 * field to a row, and one full-width Continue with a short hint above it when something is missing. Signing
 * opens a sheet with a pad drawn on by touch. Re-signing new terms uses the same steps.
 */
export function OnboardingNative({ o }: { o: Onboarding }) {
  const key = o.flow[o.idx];
  const isLast = o.idx === o.flow.length - 1;
  // Why: signing clears the membership's re-sign flag mid-way, so the steps (fixed at the start) say which this is.
  const resign = !o.flow.includes("about");
  const p = o.product;
  const hint = missing(o, key);
  const label = isLast ? (resign ? "Done" : "Enter the portal") : "Continue";
  const backAction = o.idx > 0 ? o.stepBack : o.leave;
  const link = o.membership.linkBase ? `${o.membership.linkBase}${o.membership.slug}` : null;

  let step: ReactNode = null;
  if (key === "about") {
    step = (
      <>
        <Head title={`Tell ${p} about you`} sub={`A quick application. ${p} reviews this before your link goes live.`} />
        <div className="pn-form">
          <Field id="po-ob-aud" label="Audience size">
            <input id="po-ob-aud" className="pn-inp" inputMode="numeric" autoComplete="off" placeholder="12,000" value={o.audienceSize} onChange={(e) => o.setAudienceSize(e.target.value)} />
          </Field>
          <Field id="po-ob-ch" label="Main channel">
            <input id="po-ob-ch" className="pn-inp" autoComplete="off" autoCapitalize="words" placeholder="Instagram, email, blog" value={o.mainChannel} onChange={(e) => o.setMainChannel(e.target.value)} />
          </Field>
          <Field id="po-ob-plan" label="How will you promote it?">
            <textarea id="po-ob-plan" className="pn-inp" rows={4} placeholder="A sentence or two on your plan" value={o.promoPlan} onChange={(e) => o.setPromoPlan(e.target.value)} />
          </Field>
        </div>
      </>
    );
  } else if (key === "agreement") {
    step = (
      <>
        <Head title={resign ? "Read the new terms" : "Sign the agreement"} sub={resign ? `${p} updated the partner terms. Read them, then sign.` : undefined} />
        <article className="po-doc">
          <h2>{p} Partner Agreement</h2>
          <p className="po-doc-s">Between {p} and the Partner</p>
          {agreementClauses(p).map((c) => (
            <div key={c.n}>
              <h3>
                {c.n}. {c.title}
              </h3>
              <p>{c.body}</p>
            </div>
          ))}
          <div className="po-sigrow">
            <span className="po-doc-l">Partner signature</span>
            <SignField sig={o.agreementSig} who={o.partnerName} today={o.today} onSign={() => o.openSign("agreement")} />
          </div>
        </article>
        <p className="pn-note">Secured e-signature by Flashadz. Your IP address and the time are recorded when you sign.</p>
      </>
    );
  } else if (key === "submitted") {
    step = (
      <div className="po-done">
        <span className="po-done-i">
          <Icon name="check" />
        </span>
        <h1>Application sent</h1>
        <p>{p} is reviewing you and will email when you are approved. While you wait, set up how you get paid.</p>
      </div>
    );
  } else if (key === "tax") {
    const us = o.taxForm === "W-9";
    step = (
      <>
        <Head title="Your tax form" sub={`${p} needs this to pay you and report at year end. We fill in the official form from your answers.`} />
        <div className="po-seg2" role="group" aria-label="Which form">
          <button type="button" className={us ? "on" : undefined} aria-pressed={us} onClick={() => o.setTaxForm("W-9")}>
            <b>W-9</b>
            <span>In the US</span>
          </button>
          <button type="button" className={us ? undefined : "on"} aria-pressed={!us} onClick={() => o.setTaxForm("W-8BEN")}>
            <b>W-8BEN</b>
            <span>Outside the US</span>
          </button>
        </div>
        <div className="pn-form">
          <Field id="po-ob-legal" label="Legal name">
            <input id="po-ob-legal" className="pn-inp" autoComplete="name" autoCapitalize="words" value={o.legalName} onChange={(e) => o.setLegalName(e.target.value)} />
          </Field>
          <Field id="po-ob-country" label={us ? "Country" : "Country of citizenship"}>
            <input id="po-ob-country" className="pn-inp" autoComplete="country-name" autoCapitalize="words" value={o.country} onChange={(e) => o.setCountry(e.target.value)} />
          </Field>
          {us ? (
            <Field id="po-ob-class" label="Tax classification">
              <select id="po-ob-class" className="pn-inp po-select" value={o.taxClass} onChange={(e) => o.setTaxClass(e.target.value)}>
                {(TAX_CLASSES.includes(o.taxClass) ? TAX_CLASSES : [o.taxClass, ...TAX_CLASSES]).map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </Field>
          ) : null}
          <Field id="po-ob-addr" label="Address">
            <input id="po-ob-addr" className="pn-inp" autoComplete="street-address" placeholder="Street, city, state, ZIP" value={o.taxAddress} onChange={(e) => o.setTaxAddress(e.target.value)} />
          </Field>
          <Field id="po-ob-tin" label={us ? "Taxpayer ID (SSN or EIN)" : "Foreign tax ID"}>
            <input
              id="po-ob-tin"
              className="pn-inp"
              inputMode={us ? "numeric" : "text"}
              autoComplete="off"
              placeholder={us ? "000-00-0000" : "Tax ID"}
              value={o.taxpayerId}
              onChange={(e) => o.setTaxpayerId(e.target.value)}
            />
          </Field>
        </div>
        <label className="po-check">
          <input type="checkbox" checked={o.certified} onChange={(e) => o.setCertified(e.target.checked)} />
          <span className="po-box">
            <Icon name="check" />
          </span>
          <span>Under penalties of perjury, I certify the information above is true and correct.</span>
        </label>
        <div className="po-taxsig">
          <span className="po-doc-l">Signature, dated {o.today}</span>
          <SignField sig={o.taxSig} who={o.partnerName} today={o.today} onSign={() => o.openSign("tax")} />
        </div>
        <p className="pn-note">We make your signed form and share it with {p}. No upload needed.</p>
      </>
    );
  } else if (key === "payout") {
    const email = o.payKind !== "bank";
    step = (
      <>
        <Head title="How you get paid" sub={`${p} sends your rewards here. You can change it any time.`} />
        <Segmented<PayKind>
          label="How you want to be paid"
          value={o.payKind}
          onChange={o.setPayKind}
          options={(["paypal", "bank", "wise"] as PayKind[]).map((k) => ({ value: k, label: PAY[k].seg }))}
        />
        <div className="pn-form">
          <Field id="po-ob-pay" label={PAY[o.payKind].label}>
            <input
              id="po-ob-pay"
              key={o.payKind}
              className="pn-inp"
              type={email ? "email" : "text"}
              inputMode={email ? "email" : "numeric"}
              autoComplete={email ? "email" : "off"}
              autoCapitalize="off"
              spellCheck={false}
              placeholder={email ? "name@email.com" : "Bank account number"}
              value={o.payValue}
              onChange={(e) => o.setPayValue(e.target.value)}
            />
          </Field>
        </div>
        <p className="pn-note">{p} pays you directly. Flashadz records what you are owed, it never holds your money.</p>
      </>
    );
  } else {
    step = resign ? (
      <div className="po-done">
        <span className="po-done-i">
          <Icon name="check" />
        </span>
        <h1>Signed</h1>
        <p>Your link stays active.</p>
      </div>
    ) : (
      <>
        <div className="po-done">
          <span className="po-done-i">
            <Icon name="check" />
          </span>
          <h1>You are all set</h1>
          <p>Start sharing your link now. Your rewards unlock once {p} approves you.</p>
        </div>
        {link ? (
          <section className="pn-card po-link">
            <div className="po-k">Your link</div>
            <div className="po-url">
              <Icon name="link" />
              <span>{link.replace(/^https?:\/\//, "")}</span>
            </div>
            <button type="button" className="pn-btn soft wide" onClick={() => void copyLink(link)}>
              <Icon name="copy" />
              Copy link
            </button>
          </section>
        ) : null}
      </>
    );
  }

  return (
    <div className="pn-screen po-ob-s">
      <PhoneNav
        title={`Step ${o.idx + 1} of ${o.flow.length}`}
        left={
          backAction ? (
            <button type="button" className="pn-back" onClick={backAction} aria-label="Back">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 5l-7 7 7 7" />
              </svg>
            </button>
          ) : (
            <span />
          )
        }
        actions={
          <span className="po-ob-brand" title={p}>
            <ProgramTile name={p} size={28} />
          </span>
        }
      />
      <div className="po-progress" role="progressbar" aria-valuemin={1} aria-valuemax={o.flow.length} aria-valuenow={o.idx + 1} aria-label="Setup progress">
        <span>
          <i style={{ width: `${((o.idx + 1) / o.flow.length) * 100}%` }} />
        </span>
      </div>
      <div className="pn-stack po-ob">
        {o.error ? (
          <p className="po-errbox" role="alert">
            {o.error}
          </p>
        ) : null}
        {step}
      </div>
      <div className="pn-foot">
        {hint ? <p className="po-hint">{hint}</p> : null}
        <button type="button" className="pn-btn pri wide" disabled={!o.canContinue} onClick={o.advance}>
          {o.busy ? "Saving…" : label}
        </button>
      </div>
      {o.signOpen ? (
        <SignSheet
          title={o.signTarget === "tax" ? "Sign the tax form" : "Sign the agreement"}
          name={o.partnerName}
          onClose={o.closeSign}
          onAdopt={o.onAdopt}
        />
      ) : null}
    </div>
  );
}
